import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
	Component,
	computed,
	effect,
	inject,
	OnInit,
	signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ComponentsModule } from '@components/components.module';
import { BaseComponent } from '@core/base.component';
import {
	ClientSubType,
	EmailRecipientType,
} from '@models/global-email-templates';
import { FormInstance } from '@models/global-forms';
import { MatterBusinessUser } from '@models/matter';
import {
	MatterPackageContent,
	MatterPackageContentDocumentType,
	MatterPackageContentType,
} from '@models/matter-package';
import { SYNC_FLOW_TYPES } from '@models/mortgage';
import { dispatch, select, Store } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import {
	DocxDocumentService,
	MergeDocumentsRequest,
} from '@services/docx-document/docx-document.service';
import { MatterPackagesService } from '@services/matter-packages/matter-packages.service';
import { NavigationService } from '@services/nav/nav.service';
import { BusinessFeatureFlagSelectors, BusinessState } from '@state/business';
import { BusinessBaseDocxsState } from '@state/business/word/business-base-docxs/business-base-docxs.state';
import { MatterPackagesDispatch } from '@state/matter/matter-packages/matter-packages.dispatch';
import { MatterWordDocumentsDispatch } from '@state/matter/matter-word-documents/matter-word-documents.dispatch';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { EmailAttachment } from 'src/app/shared/api-repos/email-integration/email-integration.models';
import { EmailTemplatesDrawerComponent } from 'src/app/shared/components/email-templates-drawer/email-templates-drawer.component';
import { getMortgagePrimaryLender } from 'src/app/shared/core/util/mortgage';
import {
	blobToBase64,
	extractBase64Data,
} from 'src/app/shared/core/util/value';
import { EmailTemplatesSelectors } from 'src/app/shared/state/email-templates/email-templates.selectors';
import { ExportPackage } from 'src/app/shared/state/matter/matter-packages/matter-packages.actions';
import { MatterWordDocumentWithMetadata } from 'src/app/shared/state/matter/matter-word-documents/matter-word-documents.selectors';
import { MatterMortgagesState } from 'src/app/shared/state/matter/matter_mortgages';
import { ButtonComponent } from 'src/app/ui/button/button.component';
import { ContentHeaderComponent } from 'src/app/ui/content-header/content-header.component';
import { AnalyticsContext } from 'src/app/ui/doc-list-item/doc-list-item.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import {
	DropdownComponent,
	DropdownItem,
} from 'src/app/ui/dropdown/dropdown.component';
import { UI_EMPTY_PRESET } from 'src/app/ui/empty/empty.component';
import { ColSpanDirective } from 'src/app/ui/grid/col-span.directive';
import { IconsModule } from 'src/app/ui/icons/icons.module';
import { ModalService } from 'src/app/ui/modal/modal.service';
import { UI_TYPES } from 'src/app/ui/modal/modal.types';
import { PageBarComponent } from 'src/app/ui/page-bar/page-bar.component';
import { ToastService } from 'src/app/ui/toast/toast.service';
import {
	SendPackageToLenderComponentDialogDataDemo,
	SendPackageToLenderDemoComponent,
} from '../../../mortgages/closer-sync/components/closer-sync-documents/closer-sync-generate-package/components/send-package-to-lender-demo/send-package-to-lender-demo.component';
import {
	LenderDocument,
	MortgageStepType,
} from '../../../mortgages/closer-sync/components/closer-sync-documents/closer-sync-generate-package/components/send-package-to-lender/send-package-to-lender.component';
import { MatterPackageWithStatusAndDocuments } from '../../documents-v2.selectors';
import { EditPackageConfirmModalComponent } from '../edit-package-confirm-modal/edit-package-confirm-modal.component';
import { MatterPackagesSelectors } from '../matter-packages.selectors';
import { MatterPackagePreviewComponent } from './matter-package-preview/matter-package-preview.component';
import {
	DocumentToRenderPayload,
	MatterPackageDocumentListComponent,
} from './matter-package-document-list/matter-package-document-list.component';
import { SignItConnectionFlowService } from 'src/app/shared/services/signit-connection-flow/signit-connection-flow.service';
import { TestHookDirective } from '@core/ui';
import { TestHooks } from './matter-package.hooks';
import {
	createTrustLedgerPackageWarning,
	logTrustLedgerWarning,
} from '@state/matter/matter-document-packages-aggregator/trust-ledger-package-warning.utils';
import {
	AlertComponent,
	ALERT_TYPES,
	CALL_TO_ACTION_TYPES,
} from 'src/app/ui/alert/alert.component';
import { UserProfileState } from 'src/app/shared/state/user-profile';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { OverwriteFields } from '@state/user-profile/actions';
interface VM {
	package: MatterPackageWithStatusAndDocuments;
	editingName: boolean;
	selectedDocumentIds: string[];
	docxPreviewDocId: string | null;
	renamingDocumentId: string | null;
	uploadedDocPreviewSourcePath: string | null;
	isPrinting: boolean;
	isEditingMergedDoc: boolean;
	form: FormInstance;
}

const defaultVM: VM = {
	package: null,
	editingName: false,
	selectedDocumentIds: [],
	docxPreviewDocId: null,
	renamingDocumentId: null,
	isPrinting: false,
	isEditingMergedDoc: false,
	form: null,
	uploadedDocPreviewSourcePath: null,
};

@Component({
	selector: 'app-matter-package',
	templateUrl: './matter-package.component.html',
	imports: [
		CommonModule,
		ButtonComponent,
		ColSpanDirective,
		PageBarComponent,
		ContentHeaderComponent,
		IconsModule,
		DragDropModule,
		DropdownComponent,
		ComponentsModule,
		MatterPackagePreviewComponent,
		MatterPackageDocumentListComponent,
		TestHookDirective,
		AlertComponent,
	],
	styles: [
		`
			.cdk-drag-placeholder {
				opacity: 0 !important;
			}
		`,
	],
})
export class MatterPackageComponent
	extends BaseComponent<VM>
	implements OnInit
{
	testHooks = TestHooks;
	private ExportPackage = dispatch(ExportPackage);
	public matterId = this.store.selectSnapshot(SelectedMatter.id);
	public ledgerUrl = UrlService.Matter_Ledger(this.matterId);
	public matterSource = this.store.selectSnapshot(SelectedMatter.matterSource);
	private OverwriteFields = dispatch(OverwriteFields);
	public wordDocumentStyles = this.store.selectSnapshot(
		BusinessState.getBusinessWordDocumentStyles,
	);
	public getBaseDocxPathForOfficeFn = this.store.selectSnapshot(
		BusinessBaseDocxsState.getBaseDocxPathForOffice,
	);
	public officeId = this.store.selectSnapshot(SelectedMatter.officeId);
	public existingDocIds$ = this.vm$.pipe(
		map((vm) => vm.package?.contents?.map((c) => c.id) || []),
	);
	public businessId = this.store.selectSnapshot(BusinessState.businessId);
	public signItFeatureFlagConnected_ = select(
		BusinessFeatureFlagSelectors.sc16163EnableSignIt,
	);
	public documentMergeEditingEnabled = this.store.selectSnapshot(
		BusinessFeatureFlagSelectors.isDocumentMergeEditingEnabled,
	);

	private getPackageById = select(MatterPackagesSelectors.getMatterPackageById);

	emailTemplateOptionsByRecipient_ = select(
		EmailTemplatesSelectors.$emailTemplateOptionsByRecipient,
	);

	outlookIntegrationEnabled_ = select(
		BusinessFeatureFlagSelectors.isOutlookIntegrationEnabled,
	);
	isSignItConnected_ = select(BusinessState.isSignITSelected);

	public showPrepareESigningButton = computed(() => {
		return this.isSignItConnected_() && this.signItFeatureFlagConnected_();
	});

	loadingEmailing_ = signal(false);
	loadingSendingToLender_ = signal(false);
	get loadingEmailing() {
		return this.loadingEmailing_();
	}
	set loadingEmailing(value) {
		this.loadingEmailing_.set(value);
	}

	private signItConnectService = inject(SignItConnectionFlowService);
	readonly ANALYTIC_CONTEXT = AnalyticsContext;
	readonly UI_EMPTY_PRESET = UI_EMPTY_PRESET;
	readonly packageId_ = signal<string | null>(null);

	// Memoization for contentMap
	private _contentMap: Map<string, MatterPackageContent> | null = null;
	private _lastPackageReference: MatterPackageWithStatusAndDocuments | null =
		null;

	private get contentMap(): Map<string, MatterPackageContent> {
		// Invalidate cache if package reference changed
		if (this._lastPackageReference !== this.VM.package) {
			this._contentMap = null;
			this._lastPackageReference = this.VM.package;
		}

		if (!this._contentMap) {
			this._contentMap = new Map(
				this.VM.package?.contents?.map((c) => [c.id, c]) || [],
			);
		}

		return this._contentMap;
	}

	/**
	 * Returns true if the package has no documents
	 */
	get hasNoDocuments(): boolean {
		return (
			!this.VM.package?.documents || this.VM.package.documents.length === 0
		);
	}

	/**
	 * Returns true if the package has no Word documents
	 */
	get hasNoWordDocuments(): boolean {
		const documents = this.VM.package?.documents || [];

		const wordDocuments = documents.filter((doc) => {
			const content = this.contentMap.get(doc.id);
			return content?.documentType === MatterPackageContentDocumentType.WORD;
		});

		return wordDocuments.length === 0;
	}

	packageDropdownItems: DropdownItem[] = [
		{
			id: 'rename',
			title: {
				label: 'Rename',
				icon: 'cursor-text',
			},
			action: () => {
				this.editName();
			},
		},
		{
			id: 'delete',
			title: {
				label: 'Delete',
				icon: 'trash',
			},
			type: UI_TYPES.DANGER,
			action: () => {
				this.deletePackage();
			},
		},
	];

	readonly ALERT_TYPES = ALERT_TYPES;
	readonly ALERT_CLOSE_TYPE = CALL_TO_ACTION_TYPES.CLOSE;

	readonly trustLedgerWarningState = select(
		UserProfileState.getDismissedTrustLedgerDifference,
	);
	private tlWarning = createTrustLedgerPackageWarning(this.packageId_);

	isTrustLedgerWarningVisible = computed(() => {
		const warning = this.tlWarning();
		const lastDifference = this.trustLedgerWarningState();

		if (!warning.shouldShowWarning) {
			return false;
		}

		if (lastDifference === undefined) {
			return true;
		}

		return warning.difference !== lastDifference;
	});

	constructor(
		private route: ActivatedRoute,
		private drawerService: DrawerService,
		private matterPackagesDispatch: MatterPackagesDispatch,
		private navService: NavigationService,
		private matterPackagesService: MatterPackagesService,
		private matterWordDocumentsDispatch: MatterWordDocumentsDispatch,
		private modalService: ModalService,
		private analytics: AnalyticsService,
		private store: Store,
		private docxDocumentService: DocxDocumentService,
		private toastService: ToastService,
		private dialog: MatDialog,
	) {
		super(defaultVM);

		effect(() => {
			const packageId = this.packageId_();
			if (!packageId) return;

			this.VM = {
				package: this.getPackageById()(packageId) ?? null,
			};
		});

		logTrustLedgerWarning(this.tlWarning);
	}

	ngOnInit() {
		const packageId = this.route.snapshot.paramMap.get('packageId');

		const shouldOpenDrawer = !!localStorage.getItem(
			'matter-packages-open-drawer',
		);
		if (shouldOpenDrawer) {
			localStorage.removeItem('matter-packages-open-drawer');
			setTimeout(() => {
				this.openDocumentsDrawer();
			}, 10);
		}

		this.packageId_.set(packageId);
	}

	dismissTrustLedgerWarning() {
		this.OverwriteFields({
			field: 'settings.hideNotifications.dismissedTLDifference',
			value: this.tlWarning().difference,
		});
	}

	exportPackage() {
		this.analytics.logEvent(AnalyticsEvent.MATTER_BUILDER_EXPORT);
		this.ExportPackage(this.VM.package.id);
	}

	// TODO: Refactor to a word documents service so it can be used in other places
	async printPackage() {
		this.VM = {
			isPrinting: true,
		};
		await this.matterPackagesService.printPackage(this.VM.package.id);
		this.VM = {
			isPrinting: false,
		};
	}

	editName() {
		this.VM = {
			editingName: true,
		};
	}

	renameDocument(docId: string) {
		this.VM = {
			renamingDocumentId: docId,
		};
	}

	onDocumentTitleChange(newTitle: string) {
		this.matterWordDocumentsDispatch.UpdateDocument({
			...this.VM.package.documents.find(
				(doc) => doc.id === this.VM.renamingDocumentId,
			).document,
			title: newTitle,
		});
		this.VM = {
			renamingDocumentId: null,
		};
	}

	goBack() {
		this.navService.goToMatter_Documents_Packages(this.matterId);
	}

	updateName(newName: string) {
		this.analytics.logEvent(AnalyticsEvent.MATTER_BUILDER_KEBAB_RENAME);

		this.VM = { editingName: false };

		this.matterPackagesDispatch.UpdatePackageName({
			id: this.VM.package.id,
			packageName: newName,
		});
	}

	setVMDocuments(vmDocs: DocumentToRenderPayload) {
		this.VM = {
			form: null,
			docxPreviewDocId: null,
			uploadedDocPreviewSourcePath: null,
			...vmDocs,
		};
	}

	onAddDocument() {
		this.analytics.logEvent(AnalyticsEvent.MATTER_BUILDER_ADD_DOCUMENTS);
		this.openDocumentsDrawer();
	}

	openDocumentsDrawer() {
		this.matterPackagesService.openDocumentsDrawer(this.packageId_());
	}

	deletePackage() {
		this.matterPackagesService.deleteMatterPackage(
			this.VM.package.id,
			this.matterId,
			AnalyticsEvent.MATTER_BUILDER_KEBAB_DELETE,
		);
	}

	preparePackage() {
		this.signItConnectService.preparePackage(this.VM.package.id);
	}

	async editMergedDocument(): Promise<void> {
		if (this.VM.isEditingMergedDoc) return;

		// Get the word documents only
		const wordDocuments = this.VM.package.documents.filter((doc) => {
			const content = this.VM.package.contents.find((c) => c.id === doc.id);
			return content?.contentType === MatterPackageContentType.MATTER_DOC;
		});

		if (wordDocuments.length === 0) {
			this.toastService.error('No Word documents in package to edit');
			return;
		}

		// Check if user has already dismissed this warning
		const ignoreWarning =
			localStorage.getItem('ignoreEditPackageInWordWarning') === 'true';

		if (!ignoreWarning) {
			// Show confirmation modal
			this.modalService.openModal(EditPackageConfirmModalComponent, {
				title: 'Warning',
				size: 'sm',
				type: UI_TYPES.WARNING,
				data: { wordDocuments: wordDocuments },
				primaryAction: {
					text: 'Continue',
					action: () => {
						this.modalService.closeModal();
						this.proceedWithEditMergedDocument(wordDocuments);
					},
				},
				secondaryAction: {
					text: 'Cancel',
					action: () => {
						this.modalService.closeModal();
					},
				},
			});
			return;
		}

		await this.proceedWithEditMergedDocument(wordDocuments);
	}

	private async proceedWithEditMergedDocument(
		wordDocuments: MatterWordDocumentWithMetadata[],
	): Promise<void> {
		this.VM = {
			isEditingMergedDoc: true,
		};

		try {
			// Call API endpoint to create merged document
			// Get the actual storage paths from the documents
			const documentPaths = wordDocuments.map(
				(doc) => doc.document.destinationStorageRef,
			);

			// Get the original document IDs for locking
			const originalDocumentIds = wordDocuments.map((doc) => doc.id);

			// Check if any document is locked before proceeding
			const documentIds = wordDocuments.map((doc) => doc.document.id);
			const lockCheckResult = await firstValueFrom(
				this.docxDocumentService.isAnyDocumentLocked(
					this.businessId,
					documentIds,
					this.matterId,
				),
			);

			if (lockCheckResult.isAnyLocked) {
				const lockedDoc = wordDocuments.find(
					(doc) => doc.document.id === lockCheckResult.lockedDocumentId,
				);
				const docTitle = lockedDoc?.document.title || 'A document';
				this.toastService.error(
					`Document "${docTitle}" is currently in use by another user. Please try again later.`,
				);
				return;
			}

			const mergeRequest: MergeDocumentsRequest = {
				documentPaths: documentPaths,
				businessId: this.businessId,
				matterId: this.matterId,
				packageId: this.VM.package.id,
				context: null,
				addSectionBreaks: true,
				maintainHeaders: true,
			};

			const result = await firstValueFrom(
				this.docxDocumentService.mergeDocuments(mergeRequest),
			);

			if (!result.success) {
				throw new Error(result.errorMessage || 'Failed to merge documents');
			}

			// Use package title for the document name
			const documentTitle = this.VM.package.packageName ?? 'New Package';

			// Open the merged document in Word using the storage path and pass original document IDs
			await this.docxDocumentService.openMergedDocumentInWord(
				result.mergedDocumentPath,
				documentTitle,
				result.mergedDocumentId,
				originalDocumentIds, // Pass the original document IDs for locking
			);

			this.toastService.success('Opening merged package in Word');
		} catch (error) {
			console.error('Error opening merged package in Word:', error);
			this.toastService.error('Error opening merged package in Word');
		} finally {
			this.VM = {
				isEditingMergedDoc: false,
			};
		}
	}

	async emailPackage() {
		this.loadingEmailing = true;
		try {
			const mergedAttachments =
				await this.matterPackagesService.getPackageAsPdfBlob(
					this.VM.package.id,
				);

			const base64Attachment = await blobToBase64(mergedAttachments);
			this.drawerService.openDrawer(EmailTemplatesDrawerComponent, {
				title: 'Send Email',
				data: {
					to: '',
					templateOptions: this.emailTemplateOptionsByRecipient_()(
						EmailRecipientType.PACKAGE_EMAIL,
						ClientSubType.ALL,
					),
					attachments: [
						{
							fileName: this.VM.package.packageName + '.pdf',
							file: extractBase64Data(base64Attachment),
						} as EmailAttachment,
					],
				},
			});
		} catch (err) {
			console.error('Error emailing package', err);
		} finally {
			this.loadingEmailing = false;
		}
	}

	/**
	 * @deprecated This method is deprecated and will be removed in future releases. It is retained temporarily for the Oct 8th, 2025 demo.
	 * **/
	async sendToSyncLender(mortgageId: string) {
		try {
			this.loadingSendingToLender_.set(true);

			const getMortgageByIdFn = this.store.selectSnapshot(
				MatterMortgagesState.getMortgage,
			);

			const lawyer: MatterBusinessUser = this.store.selectSnapshot(
				SelectedMatter.assignedLawyer,
			);
			const mortgage = getMortgageByIdFn(mortgageId);
			const primaryLender = getMortgagePrimaryLender(mortgage);
			const mortgageStep = MortgageStepType.REQUEST_FOR_FUNDS;
			const requiresLawyerSignature = true;
			const mergedAttachments =
				await this.matterPackagesService.getSyncPackageAsPdfBlob(
					this.VM.package.id,
					this.matterId,
				);

			const base64Attachment = await blobToBase64(mergedAttachments);
			const base64String = extractBase64Data(base64Attachment);

			const mergedDocuments = this.VM.package.documents.map((doc) => {
				return {
					id: doc.id,
					name: doc.document.title,
					path: doc.document.destinationStorageRef,
				} as LenderDocument;
			});
			if (!mortgage) {
				return;
			}

			const data: SendPackageToLenderComponentDialogDataDemo = {
				mortgageId: mortgage.id,
				lenderName: primaryLender?.shortName || primaryLender?.name || '',
				instructionId: mortgage.relatedMortgageId,
				lawyerId: lawyer.id,
				mortgageStep,
				requiresLawyerSignature,
				documents: mergedDocuments,
				syncFlowType: SYNC_FLOW_TYPES.FAX,
				mergedDocument: base64String,
			};

			this.dialog.open(SendPackageToLenderDemoComponent, {
				data,
				panelClass: 'no-padding',
			});
		} catch (e) {
			console.error('Error setting loading state for sending to lender', e);
		} finally {
			this.loadingSendingToLender_.set(false);
		}
	}
}
